<template>
  <ConfirmDialog ref="confirm" />
  <v-form ref="form">
    <card class="mb-3">
      <div class="cardTitle">{{ $t('Требования к участнику {_1}', { '_1': requirementsQuantityPattern }) }}</div>
      <div
        class="d-flex flex-column ga-3"
        style="max-height: 450px; overflow-y: auto;"
      >
        <div v-for="(item, index) in requirements" :key="item.id ?? item._tempId">
          <!-- Свернутый вид -->
          <div
            v-if="!item.isItemEdit"
            class="d-flex flex-row justify-space-between align-center requirement-small-block"
            :class="{ 'disabled-block': !canEdit }"
          >
            <div class="requirement-title">
              <v-icon class="mr-2">{{ getIconByType(item.type) }}</v-icon>
              {{ item.name }}
            </div>
            <div class="d-flex flex-row align-center ga-2">
              <v-btn
                variant="text"
                icon="custom:pencilIcon"
                @click="edit(item)"
                :disabled="!canEdit"
              ></v-btn>
              <v-btn
                variant="text"
                icon="custom:deleteIcon"
                @click="remove(item)"
                :disabled="!canEdit"
              ></v-btn>
            </div>
          </div>

          <!-- Развернутый вид -->
          <div
            v-else
            class="d-flex flex-column ga-3"
            :class="{ 'disabled-block': !canEdit }"
          >
            <div class="requirement-title">{{ $t('Создайте требование {_1}', { '_1': index + 1 }) }}</div>
            <div class="d-flex flex-row align-start ga-2">
            <TextArea
              style="width: 60%"
              v-model.trim="item.name"
              @change="$emit('changed')"
              :rules="[].concat(validation.required())"
              label="Наименование"
              :rows="1"
              maxRows="3"
              maxlength="255"
              :disabled="!canEdit"
            />

              <SelectInput
                style="width: 40%"
                label="Тип"
                single-line
                v-model="item.type"
                @update:model-value="val => onTypeChange(item, val)"
                :items="requirementList"
                item-title="title"
                item-value="code"
                :rules="[].concat(validation.required())"
              />
            </div>

            <div v-if="item.type === SUPPLIER_REQUIREMENT_TYPE.LIST" class="d-flex flex-column ga-3">
              <div
                v-for="(listItem, listIndex) in item.list"
                :key="listIndex"
                class="d-flex flex-row align-center ga-2"
              >
              <TextArea
                style="width: 80%"
                v-model.trim="listItem.value"
                @change="$emit('changed')"
                :rules="[].concat(validation.required())"
                :label="$t(`Значение {_1}`, {'_1': listIndex + 1})"
                :rows="1"
                maxRows="3"
                maxlength="255"
                :disabled="!canEdit"
              />
                <v-btn
                  icon="mdi-close"
                  variant="text"
                  @click="removeListItem(item, listIndex)"
                  :disabled="!canEdit || item.list.length <= 1"
                ></v-btn>
              </div>

              <div class="d-flex justify-start">
                <v-btn
                  class="text-decoration-underline"
                  color="primary"
                  variant="text"
                  @click="addListItem(item)"
                  :disabled="item.list.length >= 10 || !canEdit"
                >{{ $t('+ Добавить значение') }}</v-btn>
              </div>
            </div>

            <TextArea
              class="not-required"
              v-model.trim="item.description"
              @change="$emit('changed')"
              label="Описание (необязательно)"
              maxRows="3"
              maxlength="255"
              :disabled="!canEdit"
            />

            <div class="d-flex justify-end align-center">
              <div class="d-flex flex-row align-center ga-4">
                <v-switch
                  :disabled="!canEdit"
                  class="not-required"
                  :label="$t('Обязательность заполнения')"
                  hide-details
                  inset
                  :model-value="item.isRequired"
                  @update:model-value="val => item.isRequired = val"
                ></v-switch>
                <v-btn
                  variant="text"
                  icon="custom:deleteIcon"
                  @click="remove(item)"
                  :disabled="!canEdit"
                ></v-btn>
              </div>
            </div>

            <v-divider/>
          </div>
        </div>
      </div>

      <div class="d-flex">
        <v-btn
          prepend-icon="mdi-plus"
          color="primary"
          density="compact"
          variant="elevated"
          class="ml-4 rounded"
          size="small"
          @click="add"
          :disabled="!canAddNewRequirement || !canEdit"
        >{{ $t('Добавить') }}</v-btn>
        <v-btn
          v-if="canShowRequirementsTable"
          color="primary"
          density="compact"
          variant="outlined"
          class="ml-4 rounded"
          size="small"
          @click="removeAll"
          :disabled="!canEdit"
        >{{ $t('Удалить все') }}</v-btn>
      </div>
    </card>
  </v-form>
</template>

<script>
import Card from "@intec/vue-components/form/Card.vue";
import TextArea from "@intec/vue-components/form/TextArea.vue";
import CountInput from "@/components/form/CountInput.vue";
import validation from "@/components/validation";
import AutoCompleteInput from "@intec/vue-components/form/AutoCompleteInput.vue";
import ConfirmDialog from "@intec/vue-components/form/ConfirmDialog.vue";
import FileInput from "@intec/vue-components/form/FileInput.vue";
import SelectInput from "@intec/vue-components/form/SelectInput.vue";
import { SUPPLIER_REQUIREMENT_TYPE, getIconByType } from '@/lib/supplierRequirement';

export default {
  name: "SupplierRequirements",
  components: {
    ConfirmDialog,
    AutoCompleteInput,
    Card,
    TextArea,
    CountInput,
    SelectInput,
    FileInput
  },
  props: {
    requirements: {
      type: Array,
      default: [],
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
    types: {
      type: Array,
      required: true,
    }
  },
  emits: ['update:requirements', 'changed'],
  data() {
    return {
      SUPPLIER_REQUIREMENT_TYPE,
      documents: [],
      tempIdCounter: 0, // Счётчик для временных id
      validation,
    }
  },
  methods: {
    getIconByType,
    onTypeChange(item, val) {
      item.type = val;
      if (val === SUPPLIER_REQUIREMENT_TYPE.LIST) {
        if (!item.list || item.list.length === 0) {
          item.list = [{ id: null, value: '' }];
        }
      } else {
        item.list = [];
      }
      this.$emit('changed');
    },
    addListItem(item) {
      if (!item.list) item.list = [];
      if (item.list.length < 10) {
        item.list.push({ id: null, value: '' });
        this.$emit('changed');
      }
    },
    removeListItem(item, index) {
      if (item.list.length > 1) {
        item.list.splice(index, 1);
        this.$emit('changed');
      }
    },
    async add() {
      const { valid } = await this.validate();
      if (!valid) return;

      // Схлопываем все текущие блоки.
      this.requirements.forEach(item => {
        item.isItemEdit = false;
      });

      this.tempIdCounter++;
      const newRequirement = {
        _tempId: 'temp_' + this.tempIdCounter,
        id: null,
        name: null,
        type: SUPPLIER_REQUIREMENT_TYPE.TEXT,
        isRequired: false,
        description: null,
        isItemEdit: true,
        list: []
      };

      const updated = [...this.requirements, newRequirement];
      this.$emit('update:requirements', updated);
      this.$emit('changed');
    },
    remove(item) {
      let updated;
      if (item.id) {
        updated = this.requirements.filter(pos => pos.id !== item.id);
      } else {
        updated = this.requirements.filter(pos => pos._tempId !== item._tempId);
      }
      this.$emit('update:requirements', updated);
      this.$emit('changed');
    },
    edit(item) {
      item.isItemEdit = true;
      this.$emit('changed');
    },
    async removeAll() {
      let approve = await this.$refs.confirm.open(
          'Удаление требований к участнику',
          'Вы действительно хотите удалить все требования?',
      );
      if (approve) {
        this.$emit('update:requirements', []);
        this.$emit('changed');
      }
    },
    async validate() {
      return this.$refs.form.validate();
    },
    async resetValidation() {
      this.$refs.form.resetValidation();
    }
  },
  computed: {
    requirementList() {
      // Преобразуем types в формат для SelectInput с иконками
      return this.types.map(type => ({
        ...type,
        icon: getIconByType(type.code)
      }));
    },
    canAddNewRequirement() {
      return this.requirements.length < 255;
    },
    canShowRequirementsTable() {
      return this.requirements.length > 0;
    },
    requirementsQuantityPattern() {
      return this.requirements.length > 0 ? `(${this.requirements.length})` : '';
    }
  }
}
</script>

<style scoped>
.requirement-small-block {
  border-radius: 10px;
  border: 1px solid rgb(var(--blue-blue-secondary-hover));
  padding: 5px 12px;
}

.requirement-title {
  font-size: 16px;
  font-weight: 600;
}

.disabled-block {
  opacity: var(--v-disabled-opacity);
}

:deep(.v-label::after) {
  content: ' *';
  color: rgba(var(--error-color));
}

:deep(.not-required .v-label::after) {
  content: '';
  color: transparent;
}

/* v-switch */
:deep(.v-input--density-default) {
  --v-input-control-height: 28px;
}

:deep(.v-selection-control--density-default) {
  --v-selection-control-size: 28px;
}

:deep(.v-switch .v-label) {
  opacity: 1;
  font-family: var(--primary-font-family-name);
  font-weight: 500;
  font-size: 16px;
}

:deep(.v-switch .v-selection-control) {
  max-height: var(--v-input-control-height);
  flex-direction: row-reverse;
  gap: 10px;
}

:deep(.v-switch .v-switch__track) {
  height: 28px;
  opacity: 1;
}

:deep(.v-switch .v-selection-control.v-selection-control--dirty .v-switch__track) {
  background-color: var(--blue-blue-hover);
}

:deep(.v-switch .v-selection-control:not(.v-selection-control--dirty) .v-switch__track) {
  background-color: #c7c7c7;
}

:deep(.v-switch .v-switch__thumb) {
  transform: none;
}
</style>
